// BroadcastOverlay — DOM overlay skin for the Spectator Cam view.
//
// Renders three broadcast-TV elements when `arenaApi.broadcastActive` is true:
//   1. A pulsing red "LIVE" badge in the top-left corner of the viewport
//   2. A centred scoreboard-ticker strip along the bottom edge with the
//      current sport, difficulty, "YOU vs CPU" scores and a small clock
//   3. A subtle vignette / letterbox effect so social captures read as
//      broadcast footage instead of a normal browser screenshot
//
// The overlay is pointer-events: none so it never blocks in-scene clicks.

import React, { useEffect, useState } from "react";

import { SPORTS, DIFFICULTIES } from "../arena/arenaState";

const fontSerif = "Georgia, 'Times New Roman', serif";
const fontMono = "'JetBrains Mono', ui-monospace, monospace";

export default function BroadcastOverlay({ arenaApi }) {
  const [clock, setClock] = useState(0);
  useEffect(() => {
    if (!arenaApi.broadcastActive) {
      setClock(0);
      return undefined;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      setClock(Math.floor((Date.now() - startedAt) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [arenaApi.broadcastActive]);

  if (!arenaApi.broadcastActive) return null;

  const sport = SPORTS[arenaApi.sport] || SPORTS.basketball;
  const difficulty = DIFFICULTIES[arenaApi.difficulty] || DIFFICULTIES.basic;
  const mm = String(Math.floor(clock / 60)).padStart(2, "0");
  const ss = String(clock % 60).padStart(2, "0");

  const shell = {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    zIndex: 8,
    fontFamily: fontSerif,
    color: "#f3ead4",
  };

  const liveBadge = {
    position: "absolute",
    top: 20,
    left: 18,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 6,
    background: "rgba(239,68,68,0.92)",
    color: "#fff8ec",
    boxShadow: "0 4px 22px rgba(239,68,68,0.4)",
    fontFamily: fontMono,
    fontSize: 12,
    letterSpacing: 3,
    fontWeight: 800,
    textTransform: "uppercase",
    animation: "tab-live-pulse 1.2s ease-in-out infinite",
  };

  const chipMuted = {
    padding: "4px 10px",
    borderRadius: 6,
    background: "rgba(11,21,48,0.75)",
    border: "1px solid rgba(230,180,80,0.35)",
    color: "rgba(243,234,212,0.9)",
    fontFamily: fontMono,
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
  };

  const ticker = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 62,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(11,21,48,0.92) 45%)",
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
  };

  const tickerInner = {
    display: "flex",
    alignItems: "center",
    gap: 18,
    width: "100%",
    justifyContent: "center",
  };

  const scoreBox = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 20px",
    borderRadius: 8,
    background: "rgba(11,21,48,0.88)",
    border: "1px solid rgba(230,180,80,0.45)",
  };

  const scoreNum = {
    fontSize: 24,
    fontWeight: 800,
    color: "#e6b450",
    letterSpacing: 1,
    lineHeight: 1,
  };

  const teamLabel = {
    fontSize: 10,
    letterSpacing: 2.5,
    color: "rgba(243,234,212,0.65)",
    textTransform: "uppercase",
    marginTop: 3,
    textAlign: "center",
  };

  // Subtle letterbox bars for that cinematic broadcast feel.
  const letterbox = {
    position: "absolute",
    left: 0,
    right: 0,
    height: 28,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  };

  return (
    <div style={shell} data-testid="broadcast-overlay">
      <style>{`
        @keyframes tab-live-pulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.75; transform: scale(0.97); }
        }
      `}</style>
      {/* Top letterbox */}
      <div style={{ ...letterbox, top: 0 }} />
      {/* Bottom letterbox */}
      <div
        style={{
          ...letterbox,
          bottom: 62,
          background:
            "linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* LIVE badge */}
      <div style={liveBadge}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#fff8ec",
          }}
        />
        LIVE
      </div>

      {/* Sport + difficulty chip in the top-right */}
      <div
        style={{
          position: "absolute",
          top: 20,
          right: 18,
          display: "flex",
          gap: 6,
        }}
      >
        <div style={chipMuted}>{sport.label}</div>
        <div style={chipMuted}>{difficulty.label}</div>
      </div>

      {/* Bottom scoreboard ticker */}
      <div style={ticker}>
        <div style={tickerInner}>
          <div style={scoreBox}>
            <div>
              <div style={scoreNum}>{arenaApi.scores.player}</div>
              <div style={teamLabel}>You</div>
            </div>
            <div
              style={{
                fontFamily: fontSerif,
                fontSize: 12,
                letterSpacing: 3,
                color: "rgba(243,234,212,0.55)",
              }}
            >
              VS
            </div>
            <div>
              <div style={scoreNum}>{arenaApi.scores.cpu}</div>
              <div style={teamLabel}>CPU</div>
            </div>
          </div>
          <div
            style={{
              fontFamily: fontMono,
              fontSize: 14,
              letterSpacing: 3,
              padding: "6px 14px",
              borderRadius: 6,
              background: "rgba(11,21,48,0.75)",
              color: "#e6b450",
              border: "1px solid rgba(230,180,80,0.35)",
            }}
          >
            {mm}:{ss}
          </div>
          <div
            style={{
              fontFamily: fontSerif,
              fontSize: 11,
              letterSpacing: 2,
              color: "rgba(243,234,212,0.6)",
              textTransform: "uppercase",
            }}
          >
            The Academic Boardroom · Spatial Broadcast
          </div>
        </div>
      </div>
    </div>
  );
}
